/**
 * Reactive profile state: which casino and table are active, and the list of
 * each. Persistence lives in `db.ts`; this is the Solid-facing layer over it.
 *
 * There is always an active table. The app must never be left with no profile,
 * so every path that could clear one falls back to the built-in default.
 */

import { batch, createMemo, createSignal } from 'solid-js';
import type { CasinoProfile, TableProfile, WheelStyle } from '~/domain/types';
import {
  DEFAULT_CASINO,
  DEFAULT_TABLES,
  defaultTableFor,
  duplicateTable,
} from '~/domain/profiles';

const ACTIVE_TABLE_KEY = 'roulette-planner.activeTableId';

export interface ProfileStore {
  casinos: () => CasinoProfile[];
  tables: () => TableProfile[];
  tablesForCasino: (casinoId: string) => TableProfile[];
  activeTable: () => TableProfile;
  activeCasino: () => CasinoProfile;
  /** Tables matching a wheel style — switching style filters the choices. */
  tablesForStyle: (style: WheelStyle) => TableProfile[];

  setCasinos: (casinos: CasinoProfile[]) => void;
  setTables: (tables: TableProfile[]) => void;
  setActiveTable: (tableId: string) => void;
  /** Keep the active table consistent with the active wheel style. */
  syncToStyle: (style: WheelStyle) => void;

  upsertCasino: (casino: CasinoProfile) => void;
  upsertTable: (table: TableProfile) => void;
  removeCasino: (casinoId: string) => void;
  removeTable: (tableId: string) => void;
  /** Copies any table, not just the active one — duplication is an editing act. */
  copyTable: (table: TableProfile, name: string) => TableProfile;
}

export function createProfileStore(): ProfileStore {
  const [casinos, setCasinosSignal] = createSignal<CasinoProfile[]>([DEFAULT_CASINO]);
  const [tables, setTablesSignal] = createSignal<TableProfile[]>([
    DEFAULT_TABLES['single-zero'],
    DEFAULT_TABLES['double-zero'],
  ]);
  const [activeTableId, setActiveTableId] = createSignal<string>(
    // Remembering the last table is a UI preference, not library data, so
    // localStorage is right here — the ban in CLAUDE.md is about spreads.
    (typeof localStorage !== 'undefined' && localStorage.getItem(ACTIVE_TABLE_KEY)) ||
      DEFAULT_TABLES['single-zero'].id,
  );

  const activeTable = createMemo<TableProfile>(
    () =>
      tables().find((table) => table.id === activeTableId()) ??
      tables()[0] ??
      defaultTableFor('single-zero'),
  );

  const activeCasino = createMemo<CasinoProfile>(
    () =>
      casinos().find((casino) => casino.id === activeTable().casinoId) ??
      casinos()[0] ??
      DEFAULT_CASINO,
  );

  const selectTable = (tableId: string) => {
    setActiveTableId(tableId);
    if (typeof localStorage !== 'undefined') localStorage.setItem(ACTIVE_TABLE_KEY, tableId);
  };

  const tablesForStyle = (style: WheelStyle) =>
    tables().filter((table) => table.wheelStyle === style);

  return {
    casinos,
    tables,
    activeTable,
    activeCasino,
    tablesForCasino: (casinoId) => tables().filter((table) => table.casinoId === casinoId),
    tablesForStyle,

    setCasinos: (next) => setCasinosSignal(next),
    setTables: (next) => setTablesSignal(next),
    setActiveTable: selectTable,

    /**
     * Called when the wheel style changes. If the active table is for the other
     * style, move to one that fits — preferring a table at the same casino, so
     * switching style at a venue keeps you at that venue.
     */
    syncToStyle: (style) => {
      if (activeTable().wheelStyle === style) return;
      const candidates = tablesForStyle(style);
      const sameCasino = candidates.find((table) => table.casinoId === activeCasino().id);
      selectTable((sameCasino ?? candidates[0] ?? defaultTableFor(style)).id);
    },

    upsertCasino: (casino) =>
      setCasinosSignal((current) => {
        const index = current.findIndex((entry) => entry.id === casino.id);
        if (index === -1) return [...current, casino];
        return current.map((entry) => (entry.id === casino.id ? casino : entry));
      }),

    upsertTable: (table) =>
      setTablesSignal((current) => {
        const index = current.findIndex((entry) => entry.id === table.id);
        if (index === -1) return [...current, table];
        return current.map((entry) => (entry.id === table.id ? table : entry));
      }),

    removeCasino: (casinoId) =>
      batch(() => {
        setTablesSignal((current) => current.filter((table) => table.casinoId !== casinoId));
        setCasinosSignal((current) => current.filter((casino) => casino.id !== casinoId));
        if (!tables().some((table) => table.id === activeTableId())) {
          selectTable(DEFAULT_TABLES['single-zero'].id);
        }
      }),

    removeTable: (tableId) =>
      batch(() => {
        setTablesSignal((current) => current.filter((table) => table.id !== tableId));
        if (activeTableId() === tableId) {
          selectTable(tables()[0]?.id ?? DEFAULT_TABLES['single-zero'].id);
        }
      }),

    copyTable: (table, name) => {
      const copy = duplicateTable(table, name);
      setTablesSignal((current) => [...current, copy]);
      return copy;
    },
  };
}
